import React, { useEffect, useMemo, useState } from 'react';
import { Appointment, Venue, Service } from '@/types';
import { Calendar, Clock, BarChart3, CheckCircle, Filter, AlertTriangle } from 'lucide-react';
import { StatusBadge } from '@shared/components/common/StatusBadge';
import { Table, TBody, TD, TH, THead, TR } from '@shared/components/ui/Table';
import { Select } from '@shared/components/ui/Select';
import { Pagination } from '@shared/components/common/Pagination';
import { api } from '@/services/api';
import { formatDateOnly, normalizeDateOnly } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { buildMapById } from '@/lib/collections';

interface ReportsTabProps {
  venues: Venue[];
  services: Service[];
}

export const ReportsTab: React.FC<ReportsTabProps> = ({ venues, services }) => {
  const [selectedReportVenueId, setSelectedReportVenueId] = useState<string>('all');
  const [todaysAppointments, setTodaysAppointments] = useState<Appointment[]>([]);
  const [todaysTotalCount, setTodaysTotalCount] = useState(0);
  const [todayPage, setTodayPage] = useState(1);
  const [todayPageSize, setTodayPageSize] = useState(25);
  const [isLoadingToday, setIsLoadingToday] = useState(false);
  const [todayError, setTodayError] = useState('');

  const [weeklyCounts, setWeeklyCounts] = useState<{ day: string; count: number }[]>([]);
  const [isLoadingWeekly, setIsLoadingWeekly] = useState(false);

  const [totalBookedCount, setTotalBookedCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const servicesById = useMemo(() => buildMapById(services), [services]);
  const venuesById = useMemo(() => buildMapById(venues), [venues]);

  const today = new Date();
  const todayStr = formatDateOnly(today);

  useEffect(() => {
    setTodayPage(1);
  }, [selectedReportVenueId]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingToday(true);
    setTodayError('');

    api
      .listStaffBookings({
        page: todayPage,
        pageSize: todayPageSize,
        dateFrom: todayStr,
        dateTo: todayStr,
        venueId: selectedReportVenueId === 'all' ? undefined : selectedReportVenueId
      })
      .then((data) => {
        if (!isActive) return;
        setTodaysAppointments(data.items || []);
        setTodaysTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setTodaysAppointments([]);
        setTodaysTotalCount(0);
        setTodayError(normalizeErrorMessage(err, 'Failed to load today’s schedule.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingToday(false);
      });

    return () => {
      isActive = false;
    };
  }, [todayPage, todayPageSize, todayStr, selectedReportVenueId]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingWeekly(true);
    const start = new Date();
    const end = new Date();
    end.setDate(start.getDate() + 6);
    const dateFrom = formatDateOnly(start);
    const dateTo = formatDateOnly(end);

    api
      .listStaffBookings({
        page: 1,
        pageSize: 500,
        dateFrom,
        dateTo,
        venueId: selectedReportVenueId === 'all' ? undefined : selectedReportVenueId
      })
      .then((data) => {
        if (!isActive) return;
        const items = data.items || [];
        const counts = [];
        for (let i = 0; i < 7; i += 1) {
          const d = new Date();
          d.setDate(d.getDate() + i);
          const dateValue = formatDateOnly(d);
          const count = items.filter((a) => normalizeDateOnly(a.date) === dateValue).length;
          counts.push({ day: d.toLocaleDateString('en-GB', { weekday: 'short' }), count });
        }
        setWeeklyCounts(counts);
      })
      .catch(() => {
        if (!isActive) return;
        setWeeklyCounts([]);
      })
      .finally(() => {
        if (isActive) setIsLoadingWeekly(false);
      });

    return () => {
      isActive = false;
    };
  }, [selectedReportVenueId]);

  const sortedTodaysAppointments = [...todaysAppointments].sort((a, b) =>
    String(a.time || '').localeCompare(String(b.time || ''))
  );

  useEffect(() => {
    let isActive = true;
    setIsLoadingStats(true);
    const venueId = selectedReportVenueId === 'all' ? undefined : selectedReportVenueId;
    Promise.all([
      api.listStaffBookings({ page: 1, pageSize: 1, status: 'booked', venueId }),
      api.listStaffBookings({ page: 1, pageSize: 1, status: 'completed', venueId })
    ])
      .then(([booked, completed]) => {
        if (!isActive) return;
        setTotalBookedCount(Number(booked.totalCount || 0) + Number(completed.totalCount || 0));
        setCompletedCount(Number(completed.totalCount || 0));
      })
      .catch(() => {
        if (!isActive) return;
        setTotalBookedCount(0);
        setCompletedCount(0);
      })
      .finally(() => {
        if (isActive) setIsLoadingStats(false);
      });

    return () => {
      isActive = false;
    };
  }, [selectedReportVenueId]);

  return (
    <div className="animate-fade-in space-y-8">
      {/* Global Filter & Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h3 className="text-xl font-bold text-slate-800">Dashboard Reports</h3>
        <div className="flex items-center gap-2 bg-white p-1 rounded-lg border shadow-sm">
          <div className="px-3 py-2 text-slate-500 bg-slate-50 rounded-md border border-slate-100 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            <span className="text-xs font-bold uppercase whitespace-nowrap">Filter Site</span>
          </div>
          <Select
            value={selectedReportVenueId}
            onChange={(e) => setSelectedReportVenueId(e.target.value)}
            className="border-none bg-transparent focus:ring-0 cursor-pointer text-slate-700 font-medium"
          >
            <option value="all">All Locations</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">Total Bookings (All Time)</p>
            <h3 className="text-3xl font-bold text-slate-900 mt-1">
              {isLoadingStats ? '—' : totalBookedCount}
            </h3>
          </div>
          <div className="bg-teal-50 p-3 rounded-full text-teal-600">
            <Calendar className="w-8 h-8" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">Bookings Today</p>
            <h3 className="text-3xl font-bold text-slate-900 mt-1">
              {isLoadingToday ? '—' : todaysTotalCount}
            </h3>
          </div>
          <div className="bg-blue-50 p-3 rounded-full text-blue-600">
            <Clock className="w-8 h-8" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">Completed</p>
            <h3 className="text-3xl font-bold text-slate-900 mt-1">
              {isLoadingStats ? '—' : completedCount}
            </h3>
          </div>
          <div className="bg-green-50 p-3 rounded-full text-green-600">
            <BarChart3 className="w-8 h-8" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Today's Schedule */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden h-fit">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center">
            <h3 className="font-bold text-lg text-slate-800">Today's Schedule</h3>
          </div>
          <div className="p-0">
            {isLoadingToday ? (
              <div className="p-8 text-center text-slate-500">Loading today’s schedule...</div>
            ) : todayError ? (
              <div className="p-8 text-center text-red-500">{todayError}</div>
            ) : todaysAppointments.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No appointments scheduled for today at this location.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="w-full text-left">
                  <THead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold">
                    <TR>
                      <TH className="px-6 py-3 text-slate-500">Time</TH>
                      <TH className="px-6 py-3 text-slate-500">Customer</TH>
                      <TH className="px-6 py-3 text-slate-500">Company</TH>
                      <TH className="px-6 py-3 text-slate-500">Service</TH>
                      <TH className="px-6 py-3 text-slate-500">Venue</TH>
                      <TH align="center" className="px-6 py-3 text-slate-500">
                        Reminder
                      </TH>
                      <TH className="px-6 py-3 text-slate-500">Status</TH>
                    </TR>
                  </THead>
                  <TBody className="divide-y divide-slate-100">
                    {sortedTodaysAppointments.map((app) => {
                      const reminderStatus = app.reminderStatus || 'pending';
                      const reminderIcon =
                        reminderStatus === 'sent' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : reminderStatus === 'failed' ? (
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                        ) : (
                          <span className="text-slate-300">-</span>
                        );

                      return (
                        <TR key={app.id} className="hover:bg-slate-50">
                          <TD className="px-6 py-4 font-medium">{app.time}</TD>
                          <TD className="px-6 py-4">{app.customerName}</TD>
                          <TD className="px-6 py-4 text-sm text-slate-600">
                            {app.companyName || '—'}
                          </TD>
                          <TD className="px-6 py-4 text-sm text-slate-600">
                            {servicesById.get(app.serviceId)?.name || app.serviceName || '—'}
                          </TD>
                          <TD className="px-6 py-4 text-xs text-slate-500">
                            {venuesById.get(app.venueId)?.city || app.venueName || '—'}
                          </TD>
                          <TD align="center" className="px-6 py-4">
                            <div className="flex justify-center">{reminderIcon}</div>
                          </TD>
                          <TD className="px-6 py-4">
                            <StatusBadge status={app.status} className="px-2 py-1" />
                          </TD>
                        </TR>
                      );
                    })}
                  </TBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        {/* Weekly Outlook */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 h-fit">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-slate-500" /> Weekly Outlook
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            Appointments for next 7 days based on selected filter.
          </p>
          <div className="space-y-3">
            {weeklyCounts.map((day, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="w-10 text-xs font-bold text-slate-500 uppercase">{day.day}</span>
                <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-teal-500 rounded-full"
                    style={{ width: `${Math.min(100, (day.count / 20) * 100)}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium w-6 text-right">{day.count}</span>
              </div>
            ))}
            {isLoadingWeekly && (
              <div className="text-xs text-slate-400">Loading weekly totals...</div>
            )}
            {!isLoadingWeekly && weeklyCounts.length === 0 && (
              <div className="text-xs text-slate-400">No upcoming bookings.</div>
            )}
          </div>
        </div>
      </div>
      <Pagination
        page={todayPage}
        pageSize={todayPageSize}
        totalCount={todaysTotalCount}
        onPageChange={setTodayPage}
        onPageSizeChange={(size) => {
          setTodayPageSize(size);
          setTodayPage(1);
        }}
        isLoading={isLoadingToday}
      />
    </div>
  );
};
